import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Formik } from 'formik';
import * as Yup from 'yup';

import BudgetStructureBulkUpload from './BudgetStructureBulkUpload';

import { modalOptions } from 'Helpers/modalOptions';

import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import SelectField from 'CommonComponents/SelectField';
import Tabs from 'CommonComponents/Tabs';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextFormField from 'CommonComponents/TextFormField';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

// ─────────────────────────────────────────────────────────────────────────────
// Validation schemas
// ─────────────────────────────────────────────────────────────────────────────

const AddSingleSchema = Yup.object().shape({
    item_type: Yup.string().oneOf(['department', 'costcenter']).required(),
    type: Yup.string().oneOf(['Expense', 'Revenue']).required($t('Type is required')),
    name: Yup.string().required($t('Name is required')),
    department_id: Yup.number()
        .nullable()
        .when('item_type', {
            is: 'costcenter',
            then: (schema) => schema.required($t('Parent department is required')),
        }),
    description: Yup.string().nullable(),
    assumptions: Yup.string().nullable(),
    dependencies: Yup.string().nullable(),
    exclusions: Yup.string().nullable(),
});

const UpdateExistingSchema = Yup.object().shape({
    description: Yup.string().nullable(),
    assumptions: Yup.string().nullable(),
    dependencies: Yup.string().nullable(),
    exclusions: Yup.string().nullable(),
});

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const ITEM_TYPE_DEPT = 'department';
const ITEM_TYPE_CC = 'costcenter';
const TYPE_EXPENSE = 'Expense';
const TYPE_REVENUE = 'Revenue';

const TAB_ADD_SINGLE = 'add-single';
const TAB_BULK_UPLOAD = 'bulk-upload';
const TAB_UPDATE_EXISTING = 'update-existing';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reports a tab's unsaved-changes (dirty) state up to the parent modal so it
 * can prompt before switching tabs. Renders nothing.
 */
function DirtyTracker({ dirty, onDirtyChange }) {
    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tab A — Add single dept or CC.
 */
function AddSingleTab({ addSingleUrl, departments, revDepartments, createPermissions, onSuccess, onDirtyChange, projectLabel }) {
    const toast = useToast();

    // Only the item / type pairs the user's role may create are offered, so a
    // custom role never picks an option the server refuses.
    const allowedTypes = (itemType) => [TYPE_EXPENSE, TYPE_REVENUE].filter((type) => createPermissions?.[itemType]?.[type] ?? true);
    const canCreateDepartment = allowedTypes(ITEM_TYPE_DEPT).length > 0;
    const canCreateCostcenter = allowedTypes(ITEM_TYPE_CC).length > 0;
    const initialItemType = canCreateDepartment ? ITEM_TYPE_DEPT : ITEM_TYPE_CC;

    const typeLabels = { [TYPE_EXPENSE]: $t('Expense'), [TYPE_REVENUE]: $t('Revenue') };

    return (
        <Formik
            initialValues={{
                item_type: initialItemType,
                type: allowedTypes(initialItemType)[0] ?? TYPE_EXPENSE,
                name: '',
                department_id: null,
                description: '',
                assumptions: '',
                dependencies: '',
                exclusions: '',
            }}
            validationSchema={AddSingleSchema}
            onSubmit={(values, actions) => {
                axios
                    .post(addSingleUrl, values)
                    .then(() => {
                        actions.resetForm();
                        toast.success($t('Item added successfully.'));
                        onSuccess && onSuccess();
                    })
                    .catch((e) => {
                        if (e?.response?.data?.errors) {
                            actions.setErrors(e.response.data.errors);
                        } else {
                            toast.error($t('Something went wrong.'));
                        }
                    })
                    .finally(() => actions.setSubmitting(false));
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => {
                const isCostcenter = values.item_type === ITEM_TYPE_CC;
                const isExpense = values.type === TYPE_EXPENSE;
                const typeOptions = allowedTypes(values.item_type).map((type) => ({ value: type, label: typeLabels[type] }));

                const selectItemType = (itemType) => {
                    setFieldValue('item_type', itemType);
                    setFieldValue('department_id', null);
                    if (!allowedTypes(itemType).includes(values.type)) {
                        setFieldValue('type', allowedTypes(itemType)[0]);
                    }
                };
                const deptOptions = isExpense
                    ? departments.map((d) => ({ value: d.id, label: d.name }))
                    : revDepartments.map((d) => ({ value: d.id, label: d.name }));
                const noParentDepartments = deptOptions.length === 0;

                // Only actual data entry counts as unsaved — switching the
                // Department/Cost-centre or Type selectors is not a "change".
                const contentDirty = Boolean(
                    values.name || values.department_id || values.description || values.assumptions || values.dependencies || values.exclusions,
                );

                return (
                    <Fragment>
                        <DirtyTracker dirty={contentDirty} onDirtyChange={onDirtyChange} />
                        <div className="tw-flex tw-flex-col tw-gap-4">
                            {/* Segmented control: Department | Cost centre */}
                            <div className="tw-inline-flex tw-self-start tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-100 tw-p-1">
                                {canCreateDepartment && (
                                    <button
                                        type="button"
                                        className={`tw-px-6 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-transition-colors ${
                                            values.item_type === ITEM_TYPE_DEPT
                                                ? 'tw-bg-white tw-text-charcoal tw-shadow-sm'
                                                : 'tw-text-gray-500 hover:tw-text-charcoal'
                                        }`}
                                        aria-pressed={values.item_type === ITEM_TYPE_DEPT}
                                        onClick={() => selectItemType(ITEM_TYPE_DEPT)}
                                    >
                                        {$t('Department')}
                                    </button>
                                )}
                                {canCreateCostcenter && (
                                    <button
                                        type="button"
                                        className={`tw-px-6 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-transition-colors ${
                                            values.item_type === ITEM_TYPE_CC
                                                ? 'tw-bg-white tw-text-charcoal tw-shadow-sm'
                                                : 'tw-text-gray-500 hover:tw-text-charcoal'
                                        }`}
                                        aria-pressed={values.item_type === ITEM_TYPE_CC}
                                        onClick={() => selectItemType(ITEM_TYPE_CC)}
                                    >
                                        {$t('Cost centre')}
                                    </button>
                                )}
                            </div>

                            {/* Parent department — only for cost centres, sits above Name */}
                            {isCostcenter && (
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Parent department')} *</label>
                                    <SelectField
                                        options={deptOptions}
                                        value={values.department_id}
                                        onChange={(opt) => setFieldValue('department_id', opt ? opt.value : null)}
                                        clearable
                                        isDisabled={noParentDepartments}
                                        placeholder={$t('Select department…')}
                                    />
                                    {noParentDepartments ? (
                                        <div className="tw-rounded tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-800">
                                            <span>{$t('No category departments, please add one first')}</span>
                                            {canCreateDepartment && (
                                                <button
                                                    type="button"
                                                    className="tw-ms-1 tw-font-medium tw-text-blue hover:tw-underline"
                                                    onClick={() => selectItemType(ITEM_TYPE_DEPT)}
                                                >
                                                    {$t('Add a department')}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        touched.department_id &&
                                        errors.department_id && <div className="tw-text-red-500 tw-text-sm">{errors.department_id}</div>
                                    )}
                                </div>
                            )}

                            {/* Name + Type — side by side */}
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                <TextFormField
                                    label={$t('Name')}
                                    name="name"
                                    type="text"
                                    required
                                    placeholder={isCostcenter ? $t('Cost centre name') : $t('Department name')}
                                />
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Type')}</label>
                                    <SelectField
                                        options={typeOptions}
                                        value={values.type}
                                        onChange={(opt) => {
                                            setFieldValue('type', opt.value);
                                            setFieldValue('department_id', null);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Description — full width */}
                            <TextareaFormField label={$t('Description')} name="description" placeholder={$t('Optional')} rows={2} />

                            {/* Assumptions + Dependencies — side by side */}
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                <TextareaFormField label={$t('Assumptions')} name="assumptions" placeholder={$t('One per line')} rows={2} />
                                <TextareaFormField label={$t('Dependencies')} name="dependencies" placeholder={$t('One per line')} rows={2} />
                            </div>

                            {/* Exclusions — full width */}
                            <TextareaFormField label={$t('Exclusions')} name="exclusions" placeholder={$t('One per line')} rows={2} />
                        </div>

                        <div className="tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
                            <ModalFooter
                                startContent={projectLabel && <span className="tw-text-sm tw-text-charcoal-50 tw-whitespace-nowrap">{projectLabel}</span>}
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Add')}
                                onCancel={() => window.dispatchEvent(new Event('budget-structure-modal:close'))}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                                submitButtonVariant="primary"
                            />
                        </div>
                    </Fragment>
                );
            }}
        </Formik>
    );
}

/**
 * Tab C — Update existing.
 */
function UpdateExistingTab({ fetchItemUrl, updateItemUrl, departments, revDepartments, onSuccess, onDirtyChange, projectLabel }) {
    const toast = useToast();

    const [selectedType, setSelectedType] = useState(TYPE_EXPENSE);
    const [selectedDeptId, setSelectedDeptId] = useState(null);
    const [selectedCcId, setSelectedCcId] = useState(null);
    const [costcenters, setCostcenters] = useState([]);
    const [isFetchingCcs, setIsFetchingCcs] = useState(false);
    const [itemData, setItemData] = useState(null);
    const [isFetchingItem, setIsFetchingItem] = useState(false);
    const [formDirty, setFormDirty] = useState(false);

    const isExpense = selectedType === TYPE_EXPENSE;
    const deptOptions = isExpense ? departments.map((d) => ({ value: d.id, label: d.name })) : revDepartments.map((d) => ({ value: d.id, label: d.name }));

    // Any interaction on the tab counts as an unsaved change: picking a Type,
    // Department or Cost centre, or editing the text fields (formDirty, reported
    // by DirtyTracker inside the Formik form below).
    const selectionChanged = selectedType !== TYPE_EXPENSE || selectedDeptId !== null || selectedCcId !== null;

    // Reset the form-dirty flag whenever no editable form is on screen.
    useEffect(() => {
        if (!itemData || isFetchingItem) {
            setFormDirty(false);
        }
    }, [itemData, isFetchingItem]);

    useEffect(() => {
        onDirtyChange?.(selectionChanged || formDirty);
    }, [selectionChanged, formDirty, onDirtyChange]);

    const ccOptions = costcenters.map((c) => ({ value: c.id, label: c.name }));

    // Fetch cost centres when a department is selected
    useEffect(() => {
        if (!selectedDeptId) {
            setCostcenters([]);
            setSelectedCcId(null);
            return;
        }

        setIsFetchingCcs(true);
        const typeSlug = isExpense ? 'department' : 'revdepartment';
        const ccUrl = fetchItemUrl.replace('ITEM_TYPE', typeSlug).replace('ITEM_ID', selectedDeptId) + '/costcenters';

        axios
            .get(ccUrl)
            .then((res) => setCostcenters(res.data?.costcenters ?? []))
            .catch(() => setCostcenters([]))
            .finally(() => setIsFetchingCcs(false));
    }, [selectedDeptId, selectedType]);

    // Fetch item text fields when selection changes
    useEffect(() => {
        if (!selectedDeptId) {
            setItemData(null);
            return;
        }

        const itemId = selectedCcId ?? selectedDeptId;
        const itemType = selectedCcId ? (isExpense ? 'costcenter' : 'revcostcenter') : isExpense ? 'department' : 'revdepartment';

        setIsFetchingItem(true);
        const url = fetchItemUrl.replace('ITEM_TYPE', itemType).replace('ITEM_ID', itemId);
        axios
            .get(url)
            .then((res) => setItemData(res.data))
            .catch(() => toast.error($t('Could not load item.')))
            .finally(() => setIsFetchingItem(false));
    }, [selectedDeptId, selectedCcId, selectedType]);

    const handleUpdate = (values, actions) => {
        const itemId = selectedCcId ? selectedCcId : selectedDeptId;
        const itemType = selectedCcId ? (isExpense ? 'costcenter' : 'revcostcenter') : isExpense ? 'department' : 'revdepartment';

        const payload = { ...values };

        const url = updateItemUrl.replace('ITEM_TYPE', itemType).replace('ITEM_ID', itemId);
        axios
            .patch(url, payload)
            .then(() => {
                toast.success($t('Changes saved.'));
                // Return the tab to a pristine state so the saved selection no
                // longer counts as an unsaved change for the tab-switch guard.
                setFormDirty(false);
                setSelectedCcId(null);
                setSelectedDeptId(null);
                setSelectedType(TYPE_EXPENSE);
                setItemData(null);
                onSuccess && onSuccess();
            })
            .catch((e) => {
                if (e?.response?.data?.errors) {
                    actions.setErrors(e.response.data.errors);
                } else {
                    toast.error($t('Something went wrong.'));
                }
            })
            .finally(() => actions.setSubmitting(false));
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* Info banner */}
            <div className="tw-bg-blue-10 tw-border tw-border-blue-25 tw-rounded tw-p-3 tw-text-sm tw-text-blue">
                {$t("Edit description, assumptions, dependencies and exclusions on an existing item. Name and type can't be changed here.")}
            </div>

            {/* Type selector */}
            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                <label className="text-input__label">{$t('Type')}</label>
                <SelectField
                    options={[
                        { value: TYPE_EXPENSE, label: $t('Expense') },
                        { value: TYPE_REVENUE, label: $t('Revenue') },
                    ]}
                    value={selectedType}
                    onChange={(opt) => {
                        setSelectedType(opt.value);
                        setSelectedDeptId(null);
                        setSelectedCcId(null);
                    }}
                />
            </div>

            {/* Department + Cost centre — side by side */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label className="text-input__label">{$t('Department')}</label>
                    <SelectField
                        options={deptOptions}
                        value={selectedDeptId}
                        onChange={(opt) => {
                            setSelectedDeptId(opt ? opt.value : null);
                            setSelectedCcId(null);
                        }}
                        clearable
                        placeholder={$t('Select department…')}
                    />
                </div>

                {selectedDeptId && costcenters.length > 0 && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">
                            {$t('Cost centre')} ({$t('optional')})
                        </label>
                        <SelectField
                            options={ccOptions}
                            value={selectedCcId}
                            onChange={(opt) => setSelectedCcId(opt ? opt.value : null)}
                            clearable
                            isLoading={isFetchingCcs}
                            placeholder={$t("Edit parent department's own fields")}
                        />
                    </div>
                )}
            </div>

            {/* Text fields */}
            {itemData && !isFetchingItem && (
                <Formik
                    key={`${selectedDeptId}-${selectedCcId}`}
                    initialValues={{
                        description: itemData.description ?? '',
                        assumptions: itemData.assumptions ?? '',
                        dependencies: itemData.dependencies ?? '',
                        exclusions: itemData.exclusions ?? '',
                    }}
                    validationSchema={UpdateExistingSchema}
                    onSubmit={handleUpdate}
                    enableReinitialize
                >
                    {({ isSubmitting, dirty, handleSubmit }) => (
                        <Fragment>
                            <DirtyTracker dirty={dirty} onDirtyChange={setFormDirty} />
                            <div className="tw-flex tw-flex-col tw-gap-4">
                                <TextareaFormField label={$t('Description')} name="description" rows={2} />
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                    <TextareaFormField label={$t('Assumptions')} name="assumptions" placeholder={$t('One per line')} rows={2} />
                                    <TextareaFormField label={$t('Dependencies')} name="dependencies" placeholder={$t('One per line')} rows={2} />
                                </div>
                                <TextareaFormField label={$t('Exclusions')} name="exclusions" placeholder={$t('One per line')} rows={2} />
                            </div>

                            <div className="tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
                                <ModalFooter
                                    startContent={projectLabel && <span className="tw-text-sm tw-text-charcoal-50 tw-whitespace-nowrap">{projectLabel}</span>}
                                    cancelButtonText={$t('Cancel')}
                                    primaryButtonText={$t('Save changes')}
                                    onCancel={() => window.dispatchEvent(new Event('budget-structure-modal:close'))}
                                    handleSubmit={handleSubmit}
                                    isSubmitting={isSubmitting}
                                    submitButtonVariant="primary"
                                />
                            </div>
                        </Fragment>
                    )}
                </Formik>
            )}

            {isFetchingItem && <div className="tw-text-sm tw-text-gray-500">{$t('Loading…')}</div>}

            {!itemData && !isFetchingItem && (
                <div className="tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
                    <ModalFooter
                        startContent={projectLabel && <span className="tw-text-sm tw-text-charcoal-50 tw-whitespace-nowrap">{projectLabel}</span>}
                        cancelButtonText={$t('Cancel')}
                        primaryButtonText={$t('Save changes')}
                        primaryButtonDisabled
                        onCancel={() => window.dispatchEvent(new Event('budget-structure-modal:close'))}
                        handleSubmit={() => {}}
                        submitButtonVariant="primary"
                    />
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main modal
// ─────────────────────────────────────────────────────────────────────────────

const AddToBudgetStructureModal = ({
    addSingleUrl,
    bulkPreviewUrl,
    bulkConfirmUrl,
    templateUrl,
    fetchItemUrl,
    updateItemUrl,
    departmentsUrl,
    projectLabel,
    canImport,
    createPermissions,
}) => {
    const modal = useModal({
        options: {
            ...modalOptions(false),
            backdrop: 'dynamic',
        },
    });

    const [departments, setDepartments] = useState([]);
    const [revDepartments, setRevDepartments] = useState([]);
    const [activeTabId, setActiveTabId] = useState(TAB_ADD_SINGLE);

    // Tracks unsaved changes in the currently-visible tab so we can warn before
    // switching tabs (tab content is independent; switching discards input).
    const dirtyRef = useRef(false);

    const handleDirtyChange = useCallback((dirty) => {
        dirtyRef.current = dirty;
    }, []);

    // Gate tab switches: Tabs runs in controlled mode, so the switch only happens
    // if we update activeTabId here.
    const handleTabActivate = useCallback(
        (tabId) => {
            if (tabId === activeTabId) {
                return;
            }
            if (dirtyRef.current && !window.confirm($t('Discard changes?'))) {
                return;
            }
            dirtyRef.current = false;
            setActiveTabId(tabId);
        },
        [activeTabId],
    );

    // Load departments when modal opens
    const loadDepartments = useCallback(() => {
        axios
            .get(departmentsUrl)
            .then((res) => {
                setDepartments(res.data.departments ?? []);
                setRevDepartments(res.data.revdepartments ?? []);
            })
            .catch(() => {});
    }, [departmentsUrl]);

    // Listen for open trigger and close event
    useEffect(() => {
        const handleOpen = () => {
            dirtyRef.current = false;
            setActiveTabId(TAB_ADD_SINGLE);
            loadDepartments();
            modal.show();
        };
        const handleClose = () => modal.hide();

        window.addEventListener('budget-structure-modal:open', handleOpen);
        window.addEventListener('budget-structure-modal:close', handleClose);

        return () => {
            window.removeEventListener('budget-structure-modal:open', handleOpen);
            window.removeEventListener('budget-structure-modal:close', handleClose);
        };
    }, [modal, loadDepartments]);

    const handleSuccess = () => {
        loadDepartments();
        // Notify the page to refresh, then close the modal.
        window.dispatchEvent(new Event('budget-structure:updated'));
        dirtyRef.current = false;
        modal.hide();
    };

    const tabs = [
        {
            id: TAB_ADD_SINGLE,
            title: $t('Add single'),
            content: () => (
                <AddSingleTab
                    addSingleUrl={addSingleUrl}
                    departments={departments}
                    revDepartments={revDepartments}
                    createPermissions={createPermissions}
                    onSuccess={handleSuccess}
                    onDirtyChange={handleDirtyChange}
                    projectLabel={projectLabel}
                />
            ),
        },
        // A file can hold every item type, so the tab needs create on all of them.
        canImport && {
            id: TAB_BULK_UPLOAD,
            title: $t('Bulk upload'),
            content: () => (
                <BudgetStructureBulkUpload
                    bulkPreviewUrl={bulkPreviewUrl}
                    bulkConfirmUrl={bulkConfirmUrl}
                    templateUrl={templateUrl}
                    onSuccess={handleSuccess}
                    onDirtyChange={handleDirtyChange}
                    onCancel={() => window.dispatchEvent(new Event('budget-structure-modal:close'))}
                    projectLabel={projectLabel}
                />
            ),
        },
        {
            id: TAB_UPDATE_EXISTING,
            title: $t('Update existing'),
            content: () => (
                <UpdateExistingTab
                    fetchItemUrl={fetchItemUrl}
                    updateItemUrl={updateItemUrl}
                    departments={departments}
                    revDepartments={revDepartments}
                    onSuccess={handleSuccess}
                    onDirtyChange={handleDirtyChange}
                    projectLabel={projectLabel}
                />
            ),
        },
    ].filter(Boolean);

    return (
        <Modal id="add-to-budget-structure-modal" modal={modal} innerContainerClasses="tw-bg-[#F8F8F8]" parentContainerClasses="tw-max-w-4xl">
            {/* Header */}
            <div className="tw-mb-2">
                <ModalHeader title={$t('Add to budget structure')} close={() => modal.hide()} />
                <p className="tw-text-sm tw-text-gray-500">{$t('Create departments and cost centres, or update existing ones.')}</p>
            </div>

            {/* Tabs — each tab renders the budget/version label in its footer row */}
            <div className="modal__form-container">
                <Tabs tabs={tabs} renderStrategy="on-visit" activeTab={activeTabId} onTabActivate={handleTabActivate} />
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Mount
// ─────────────────────────────────────────────────────────────────────────────

if (document.getElementById('AddToBudgetStructureModalApp')) {
    const rootEl = document.getElementById('AddToBudgetStructureModalApp');

    ReactDOM.render(
        <App>
            <AddToBudgetStructureModal
                addSingleUrl={rootEl.getAttribute('data-add-single-url')}
                bulkPreviewUrl={rootEl.getAttribute('data-bulk-preview-url')}
                bulkConfirmUrl={rootEl.getAttribute('data-bulk-confirm-url')}
                templateUrl={rootEl.getAttribute('data-template-url')}
                fetchItemUrl={rootEl.getAttribute('data-fetch-item-url')}
                updateItemUrl={rootEl.getAttribute('data-update-item-url')}
                departmentsUrl={rootEl.getAttribute('data-departments-url')}
                projectLabel={rootEl.getAttribute('data-project-label')}
                canImport={JSON.parse(rootEl.getAttribute('data-can-import') ?? 'true')}
                createPermissions={JSON.parse(rootEl.getAttribute('data-create-permissions') ?? 'null')}
            />
        </App>,
        rootEl,
    );
}
